/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect } from 'react';
import { Input, message, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { editShare } from '~src/services/api/shares';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DB_STATE } from '~src/types/enums';
import {
  getOfferStatusVisual,
  getShareStatusVisual,
  getShareStatusVisualIntl,
} from '~src/utils/shareUtils/useShareKeysStatusUtils';
import { DbShare } from '~src/types/db.d.ts';
const { TextArea } = Input;

export type EditShareTenderModalProps = Pick<ModalProps, 'visible'> & {
  onCancel?: () => void;
  projectId: number;
  tenderId: number;
  shareKey: string;
  emailDisabled?: boolean;
  status: number;
  previousData?: DbShare;
};

export const GeneralTab: React.FC<EditShareTenderModalProps> = ({
  onCancel,
  projectId,
  tenderId,
  shareKey,
  emailDisabled,
  status,
  previousData,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // console.log(`GeneralTab`, previousData, status, shareKey);

  interface FormValues {
    name: string;
    http: string;
    phone: string;
    email: string;
    note: string;
  }

  const sharesForm = useForm<FormValues>({
    defaultValues: {
      name: '',
      http: '',
      phone: '',
      email: '',
      note: '',
    },
  });

  const [updateShareMutation, { isLoading }] = useMutation(editShare, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'questions-answering-failed' }));
      appLog('addAnswerMutation -> item', item);
      appLog('addAnswerMutation -> err', error);
    },
  });

  const isValid = () => {
    const { email } = sharesForm.getValues();
    if (!email) {
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!isValid()) return;

      // await updateShareMutation({
      //   projectId,
      //   tenderId,
      //   shareKey,
      //   data: sharesForm.getValues(),
      // });

      onCancel?.();
    } catch (err) {
      message.error(intl.formatMessage({ id: 'share-edit-failed' }));
    }
  };

  useEffect(() => {
    const values = sharesForm.getValues();
    if (previousData && !values.email) {
      sharesForm.reset({
        email: previousData?.share_key_email,
        note: previousData?.share_description,
      });
    }
  }, [previousData]);

  return (
    <div>
      <div css={styles.formContainer}>
        {previousData && (
          <div css={styles.formUpper}>
            <div css={styles.formStatus}>
              <div>{intl.formatMessage({ id: 'status' })}</div>
              {(() => {
                const { icon, color, text } = getShareStatusVisualIntl(
                  intl,
                  previousData
                );
                return (
                  <div style={{ color }}>
                    {icon} {text}
                  </div>
                );
              })()}
            </div>

            <div css={styles.formAcceptedAs}>
              <div>Invitation accepted as</div>
              <div css={styles.formAcceptedDetails}>
                <span>Name: {previousData?.share_key_title || '—'}</span>
                <span>Email: {previousData?.share_key_email || '—'}</span>
                <span>
                  Adress: {previousData?.project_data?.address || '—'}
                </span>
                <span>
                  Phone:{' '}
                  {previousData?.project_data?.share_key_user_phone || '—'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div css={styles.formItem}>
          <p css={styles.formLabel}>Invitation e-mail sent to</p>
          <div css={styles.formEditInputWrapper}>
            <Controller
              name="email"
              control={sharesForm.control}
              render={({ field }) => (
                <Input disabled={status !== 3} {...field} />
              )}
            />
            <button css={styles.formEditBtn}>
              <FontAwesomeIcon icon={['far', 'pen']} />
              Edit
            </button>
          </div>
        </div>
        <div css={styles.formItem}>
          <div css={styles.formEditInputWrapper} style={{ marginBottom: 12 }}>
            <span>{intl.formatMessage({ id: 'note' })}</span>
            <button css={styles.formEditBtn}>
              <FontAwesomeIcon icon={['far', 'pen']} />
              Edit
            </button>
          </div>
          <Controller
            name="note"
            control={sharesForm.control}
            render={({ field }) => (
              <TextArea autoSize={{ minRows: 2, maxRows: 6 }} {...field} />
            )}
          />
        </div>
        <div
          css={styles.formItem}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
          <button css={styles.formEditBtn}>OK</button>
        </div>
        <hr style={{ opacity: '20%' }} />
        {/* New Bids (Offers/Contracts) Section */}
        {previousData && previousData.offers && previousData.offers.length > 0 && (
          <div css={styles.generalFooter}>
            <p>Bids</p>
            {previousData.offers.length === 1 ? (
              // Single offer: display full-width with status and contract ID if active
              (() => {
                const offer = previousData.offers[0];
                const { color, text } = getOfferStatusVisual(intl, offer);
                return (
                  <div css={styles.contractWrapper}>
                    {offer.contract_id && (
                      <span css={styles.contractName}>
                        Contract ID: #{offer.contract_id} Ref#:
                        {offer.contract_reference}
                      </span>
                    )}
                    <div
                      css={styles.contractStatus}
                      style={{
                        border: `1px solid ${color}`,
                        borderRadius: '3px',
                        padding: '2px 5px',
                        gap: '3px',
                        alignItems: 'center',
                        color,
                      }}>
                      <strong>{text}</strong>
                    </div>
                  </div>
                );
              })()
            ) : (
              // Multiple offers: display each offer in a separate row with contract ID and status
              <div
                css={styles.multipleContractsContainer}
                style={{
                  maxHeight: '180px',
                  overflowY: 'auto',
                  paddingRight: '5px',
                }}>
                {previousData.offers.map((offer, index) => {
                  const { color, text } = getOfferStatusVisual(intl, offer);
                  return (
                    <div
                      key={index}
                      css={styles.contractWrapper}
                      style={{
                        display: 'flex',
                        gap: '5px',
                        alignItems: 'center',
                        marginBottom: '5px',
                      }}>
                      {offer.contract_id && (
                        <span css={styles.contractName}>
                          Contract ID: #{offer.contract_id} Ref#:
                          {offer.contract_reference}
                        </span>
                      )}
                      <div
                        css={styles.contractStatus}
                        style={{
                          border: `1px solid ${color}`,
                          borderRadius: '3px',
                          padding: '2px 5px',
                          gap: '3px',
                          alignItems: 'center',
                          color,
                        }}>
                        <strong>{text}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const styleFn: ThemedStyleFn = (theme) => ({
  // Form
  formContainer: {
    margin: 10,
  },
  generalFooter: {
    display: 'flex',
    flexDirection: 'column',
  },
  contractWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
  },
  contractName: {
    marginRight: 0,
    width: '25%',
  },
  contractStatus: {
    marginLeft: 0,
  },
  contractDate: {
    position: 'absolute',
    right: 0,
  },
  formEditInputWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '30px',
    // backgroundColor: 'red',
  },
  formEditBtn: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    borderRadius: 9,
    width: '50px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    type: 'button',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: theme.colors.gray,
    },
  },
  formUpper: {
    // backgroundColor: 'red',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  formStatus: {
    // backgroundColor: 'blue',
    display: 'flex',
    gap: '120px',
  },
  formAcceptedAs: {
    display: 'flex',
    gap: '30px',
    height: '100px',
  },
  formAcceptedDetails: {
    display: 'flex',
    flexDirection: 'column',
    color: 'gray',
  },
  buttonsBlock: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  leftButton: {
    marginRight: 10,
  },
  formItem: {
    marginBottom: 12,
  },
  formLabel: {
    marginBottom: 12,
  },
  cardContainer: {
    marginTop: 10,
    borderRadius: 10,
  },
  dateText: {
    textAlign: 'start',
    fontSize: 12,
    paddingRight: 8,
  },
  mergeContainer: {
    display: 'flex',
  },
  twoColumnsField: {
    width: '50%',
    margin: '4px 0px 15px -1px',
  },
});
